import { useCallback, useEffect, useRef, useState } from 'react';
import { apiInvestigados, type AchadoNoDou, type AndamentoDoDou, type BuscaNoDou, type Investigado } from '../lib/api';
import { usarEhMaster } from '../lib/papel.ts';
import { Traco } from './ImoveisDoCaso';
import '../estilos/imoveis-2026.css';

/**
 * ⭐⭐ **O DIÁRIO OFICIAL DA UNIÃO, NA QUALIFICAÇÃO DA PARTE — 19/09/2026 (PRD §48).**
 *
 * > *"pode seguir com o Diário Oficial"* — Richard. Perguntado onde queria ver o que se
 * > acha, escolheu **Qualificação das partes**: o achado pode ser de tudo — contrato com
 * > o governo, cargo público, leilão, sanção —, e ali fica junto da pessoa.
 *
 * ⛔ **Apontamento, não lançamento.** Como os sinais de blindagem: o Hub mostra o que
 * achou, com o trecho e a data, e **ninguém lança nada sozinho**. A busca é por **nome**.
 *
 * ⭐ **O grau vem do documento, e é o que separa o achado do homônimo:** o Diário publica
 * o CPF tapado (`***.456.789-**`), e a ficha do caso tem o número inteiro — conferir os
 * seis dígitos à mostra é feito aqui dentro, sem consultar nada.
 *
 * ⚠️ **A varredura demora minutos, então a tela acompanha:** o botão começa, e esta peça
 * pergunta a cada poucos segundos como vai, até acabar. Fechar o tópico não para a busca
 * — ela continua no servidor e o resultado fica guardado.
 */

const MESES_CURTOS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

const DA_SECAO: Record<string, string> = {
  DO1: 'Seção 1 · atos normativos',
  DO2: 'Seção 2 · pessoal',
  DO3: 'Seção 3 · contratos e editais',
  DO1E: 'Seção 1 · edição extra',
  DO2E: 'Seção 2 · edição extra',
  DO3E: 'Seção 3 · edição extra',
  DO1A: 'Seção 1 · suplemento',
};

const DO_GRAU: Record<AchadoNoDou['grau'], { rotulo: string; classe: string }> = {
  confirmado: { rotulo: 'confirmado', classe: 'verde' },
  provavel: { rotulo: 'provável', classe: 'ouro' },
  homonimo: { rotulo: 'de outra pessoa', classe: 'cinza' },
};

const paraDia = (iso: string): string => {
  const [a, m, d] = iso.split('-');
  return `${d}/${m}/${a}`;
};

/** "set/2026 a jun/2026" — o que já foi varrido, em uma linha. */
function periodoVarrido(busca: BuscaNoDou): string {
  if (busca.meses.length === 0) return 'nada varrido ainda';
  const ordenados = [...busca.meses].sort((x, y) => y.ano - x.ano || y.mes - x.mes);
  const nome = (m: { ano: number; mes: number }) => `${MESES_CURTOS[m.mes - 1]}/${m.ano}`;
  const novo = ordenados[0]!;
  const velho = ordenados[ordenados.length - 1]!;
  const quantos = busca.meses.length === 1 ? '1 mês' : `${busca.meses.length} meses`;
  return busca.meses.length === 1 ? `${quantos} · ${nome(novo)}` : `${quantos} · de ${nome(velho)} a ${nome(novo)}`;
}

export function DiarioDaUniao({ investigado }: { investigado: Investigado }) {
  const ehMaster = usarEhMaster();
  const [busca, setBusca] = useState<BuscaNoDou | null>(null);
  const [andamento, setAndamento] = useState<AndamentoDoDou | null>(null);
  const [aberto, setAberto] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [meses, setMeses] = useState(12);
  const vivo = useRef(true);

  const ler = useCallback(async () => {
    try {
      const r = await apiInvestigados.douDaParte(investigado.id);
      if (!vivo.current) return;
      setBusca(r.busca);
      setAndamento(r.andamento);
    } catch (e) {
      if (vivo.current) setErro(e instanceof Error ? e.message : 'Não consegui ler a busca no Diário.');
    }
  }, [investigado.id]);

  useEffect(() => {
    vivo.current = true;
    void ler();
    return () => {
      vivo.current = false;
    };
  }, [ler]);

  /*
   * ⚠️ **Só pergunta enquanto há busca correndo**, e para quando ela acaba. Um relógio
   * que nunca para é o tipo de coisa que ninguém nota e que castiga o servidor a cada
   * tela aberta.
   */
  useEffect(() => {
    if (!andamento) return;
    const t = setInterval(() => void ler(), 4000);
    return () => clearInterval(t);
  }, [andamento, ler]);

  async function procurar() {
    setErro(null);
    try {
      const r = await apiInvestigados.buscarNoDou(investigado.id, meses);
      setAndamento(r.andamento);
      setBusca(r.busca);
      setAberto(true);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não consegui começar a busca.');
    }
  }

  const achados = busca?.achados ?? [];
  const confirmados = achados.filter((a) => a.grau === 'confirmado').length;
  const estado = andamento
    ? `varrendo… ${andamento.feitos} de ${andamento.total} ${andamento.total === 1 ? 'mês' : 'meses'}`
    : busca
      ? `${periodoVarrido(busca)}${achados.length ? ` · ${achados.length} achado(s)` : ' · nada da parte'}`
      : 'ainda não procurado';

  return (
    <div className="imv imv-dou">
      <div className="imv-fontes">
        <span className="imv-rot" title="Diário Oficial da União — o arquivo aberto da Imprensa Nacional">
          Diário Oficial da União
        </span>
        <span className={`imv-fonte${busca && busca.meses.length > 0 ? (confirmados ? ' ok' : '') : ' falta'}`}>
          <i />
          {estado}
        </span>
        <span className="imv-esp" />
        {ehMaster && (
          <>
            <label className="imv-dou-periodo">
              <span className="imv-rotulo">Procurar nos últimos</span>
              <select value={meses} onChange={(e) => setMeses(Number(e.target.value))} disabled={!!andamento}>
                <option value={3}>3 meses</option>
                <option value={12}>12 meses</option>
                <option value={36}>3 anos</option>
                <option value={60}>5 anos</option>
              </select>
            </label>
            <button type="button" className="imv-bt fantasma p" disabled={!!andamento} onClick={() => void procurar()}>
              <Traco d={['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16', 'm21 21-4.35-4.35']} />
              {andamento ? 'Varrendo…' : busca ? 'Procurar mais' : 'Procurar no Diário'}
            </button>
          </>
        )}
        {(achados.length > 0 || busca?.falha) && (
          <button
            type="button"
            className={`imv-bt fantasma p${aberto ? ' aberto' : ''}`}
            aria-expanded={aberto}
            onClick={() => setAberto(!aberto)}
          >
            {aberto ? 'Fechar' : 'Ver o que apareceu'}
          </button>
        )}
      </div>

      {erro && <p className="erro">{erro}</p>}

      {aberto && (
        <div className="imv-painel">
          <p className="imv-painel-titulo">Diário Oficial da União · {investigado.nome}</p>
          {/* ⛔ A frase que impede o mal-entendido: isto é apontamento, e a busca é por nome. */}
          <p className="ajuda">
            O Hub procurou o <strong>nome</strong> no arquivo oficial do Diário. Nada entra no caso sozinho: leia o
            trecho, confira na página do próprio Diário e lance onde fizer sentido.{' '}
            {investigado.documento
              ? 'O Diário publica o CPF tapado — quando os dígitos à mostra batem com os da ficha, o achado vem como confirmado.'
              : 'Sem documento na ficha, não há como separar homônimo: todo achado fica como provável.'}
          </p>

          {/* ⚠️ Enquanto varre, a tela NÃO diz "nada consta": ainda não se sabe. E diz
              que dá para sair da tela — a busca corre no servidor, não aqui. */}
          {andamento && (
            <p className="aviso-atencao">
              Varrendo o Diário… {andamento.feitos} de {andamento.total} {andamento.total === 1 ? 'mês' : 'meses'}. Pode
              fechar esta tela: a busca continua e o resultado fica guardado.
            </p>
          )}

          {busca?.falha && <p className="aviso-atencao">{busca.falha}</p>}

          {achados.length === 0 ? (
            !andamento && (
              <p className="ajuda">
                Nada da parte no período varrido ({busca ? periodoVarrido(busca) : '—'}). Isso não quer dizer que não
                exista: quer dizer que não consta nesse período.
              </p>
            )
          ) : (
            <ul className="imv-lista imv-dou-lista">
              {achados.map((a, i) => (
                <li key={`${a.materia.id}-${i}`} className="imv-linha">
                  <div className="imv-linha-topo">
                    <span className={`imv-tag ${DO_GRAU[a.grau].classe}`}>{DO_GRAU[a.grau].rotulo}</span>
                    <b>{a.materia.identifica ?? a.materia.tipo ?? 'Ato'}</b>
                    <span className="imv-esp" />
                    <span className="dado">{paraDia(a.materia.publicadoEm)}</span>
                  </div>
                  <div className="imv-detalhe-texto">
                    <p className="imv-legenda">
                      {DA_SECAO[a.materia.secao] ?? a.materia.secao}
                      {a.materia.pagina ? ` · página ${a.materia.pagina}` : ''}
                      {a.materia.orgao ? ` · ${a.materia.orgao}` : ''}
                    </p>
                    {a.trechos.map((t, j) => (
                      <p key={j} className="imv-dou-trecho">
                        {t}
                      </p>
                    ))}
                    <p className="imv-legenda">
                      {a.porQue}
                      {a.materia.paginaNoDou && (
                        <>
                          {' · '}
                          <a href={a.materia.paginaNoDou} target="_blank" rel="noreferrer noopener">
                            ver a página no Diário
                          </a>
                        </>
                      )}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
